import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { UrlHelper } from 'src/utils/url-helper';
import { WebsiteViewService } from 'src/model/WebsiteViewService';
import { PrimitiveName } from '../utils/constants';
import { SdMetric, SdValueType, MetricsApiResponse } from '../types/metrics.types';

export interface PrimitiveMetricsData {
  metrics: SdMetric[];
  stage: string;
  isLoading: boolean;
}

// Stage these point-in-time metrics belong to (shown in the header).
const STAGE = 'WES_SNAPSHOT_GENERATION';

const VALUE_TYPES: SdValueType[] = ['PERCENTAGE', 'NUMBER', 'STRING', 'DATE'];

const toValueType = (raw?: string): SdValueType =>
  VALUE_TYPES.includes(raw as SdValueType) ? (raw as SdValueType) : 'STRING';

// Only PERCENTAGE/NUMBER carry a numeric value. Anything non-finite (the backend
// sends "-Infinity") or a non-numeric type becomes null -> shown as N/A.
const toNumericValue = (raw: number | string, valueType: SdValueType): number | null => {
  if (valueType !== 'PERCENTAGE' && valueType !== 'NUMBER') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
};

// Map the backend response onto the dashboard's SdMetric model.
export const mapMetricsResponse = (response: MetricsApiResponse): SdMetric[] =>
  (response?.metrics ?? []).map((m) => {
    const valueType = toValueType(m.metricAttributes?.valueType);
    return {
      metricName: m.name,
      description: m.metricAttributes?.description ?? '',
      metricClassification: m.metricAttributes?.metricClassification ?? '',
      valueType,
      value: toNumericValue(m.value, valueType),
    };
  });

export const usePrimitiveMetricsData = (): PrimitiveMetricsData => {
  useLocation(); // re-run when the URL (website) changes
  const website = UrlHelper.getParamValue('website');

  const { data, isFetching } = useQuery<MetricsApiResponse>({
    queryKey: ['primitiveMetrics', website, PrimitiveName.SDO],
    queryFn: async () =>
      (await new WebsiteViewService().getPrimitivePointInTimeMetrics(
        website || '',
        PrimitiveName.SDO
      )) as unknown as MetricsApiResponse,
    enabled: !!website,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: 3,
  });

  const metrics = useMemo(() => mapMetricsResponse(data ?? { metrics: [] }), [data]);

  return { metrics, stage: STAGE, isLoading: isFetching };
};
